import { AnyAction } from 'redux';
import { Store } from 'src/store/index';
import { ThunkDispatch } from 'redux-thunk';
import productUidChangedOperation from 'src/store/design/operation/productUidChangedOperation';
import sendProductUidChangedPostMessageOperation from 'src/store/design/operation/sendProductUidChangedPostMessageOperation';
import setProductBundleSelectedProductsOperation from 'src/store/productBundle/operation/setProductBundleSelectedProductsOperation';
import updateProductUidInDesignStructureOperation from 'src/store/design/operation/updateProductUidInDesignStructureOperation';
import { setIncludeDpiEnhancementAction, setIncludeCanvasHangerKitAction, setDesignQuantityAction } from 'src/store/design/slice';
import { setIncludeRemoveLogoAction } from 'src/store/upsell/slice';
import resolveBundledProductsFromUids from 'src/util/bundles/resolveBundledProductsFromUids';

interface UpsellProduct {
  aovQuantity?: number | null;
  aovDPIUpsell?: boolean | null;
  aovHangerKitUpsell?: boolean | null;
  aovRemoveLogoUpsell?: boolean | null;
}

const upgradeProductByUpsellOperation = (
  productUid: string,
  internalUid: string,
  productId: number,
  bundledProducts: any[],
  product?: UpsellProduct,
) => async (
  dispatch: ThunkDispatch<Store, undefined, AnyAction>,
  getState: () => Store,
) => {
  // Fetch the new product template and send it to the editor for design adaptation.
  // This must be called BEFORE productUidChangedOperation to avoid the internalUid
  // early-return check in sendProductUidChangedPostMessageOperation.
  // Wrapped in try-catch so a preflight API failure doesn't block the upsell —
  // the fallback path still updates the productUid in the structure.
  try {
    await dispatch(sendProductUidChangedPostMessageOperation({
      productUid,
      internalUid,
      productId,
      categoryId: window.integrationLayer.categoryId,
    }));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch product template for upsell', e);
  }

  await dispatch(productUidChangedOperation({
    productUid,
    internalUid,
    productId,
  }));

  // rewrite bundled products
  // Convert bundledProducts from AOV event format (object with UIDs) to Redux format (array with productId/bundleGroupId)
  const resolvedBundledProducts = resolveBundledProductsFromUids(bundledProducts);
  dispatch(setProductBundleSelectedProductsOperation(resolvedBundledProducts));

  // update AOV fields from product if provided
  if (product) {
    if (product.aovDPIUpsell !== undefined && product.aovDPIUpsell !== null) {
      dispatch(setIncludeDpiEnhancementAction(product.aovDPIUpsell));
    }
    if (product.aovHangerKitUpsell !== undefined && product.aovHangerKitUpsell !== null) {
      dispatch(setIncludeCanvasHangerKitAction(product.aovHangerKitUpsell));
    }
    if (product.aovRemoveLogoUpsell !== undefined && product.aovRemoveLogoUpsell !== null) {
      dispatch(setIncludeRemoveLogoAction(product.aovRemoveLogoUpsell));
    }
    if (product.aovQuantity !== undefined && product.aovQuantity !== null) {
      dispatch(setDesignQuantityAction(product.aovQuantity));
    }
  }

  // Update productUid in the host's design structure for the immediate save.
  // The editor will also adapt the structure via design.changeFormat callback,
  // but that happens asynchronously after the save.
  dispatch(updateProductUidInDesignStructureOperation(productUid));
};

export default upgradeProductByUpsellOperation;
